import type { ChallengeDetails, ChallengeType } from './challenges';
import type { ActivityType } from './talents';

export type RoomStatus = 'active' | 'completed' | 'expired';
export type RoomInviteStatus = 'available' | 'redeemed' | 'expired' | 'revoked' | 'room-closed';
export type InviteResolution =
  | { status: 'member'; roomId: string }
  | { status: 'club-member'; clubId: string }
  | { status: 'available' | 'unavailable'; target?: 'club'; title?: string; timezone?: string };
export const inviteExpirations = ['unlimited', '10m'] as const;
export type InviteExpiration = typeof inviteExpirations[number];
export const DEFAULT_INVITE_EXPIRATION: InviteExpiration = 'unlimited';

export function isInviteExpiration(value: unknown): value is InviteExpiration {
  return typeof value === 'string'
    && (inviteExpirations as readonly string[]).includes(value);
}

export function parseInviteExpiration(value: unknown): InviteExpiration | null {
  if (value === undefined) return DEFAULT_INVITE_EXPIRATION;
  return isInviteExpiration(value) ? value : null;
}

export interface RoomChallengeSnapshot extends ChallengeDetails {
  schemaVersion: 1;
  goalModel: 'per-participant';
}

export interface RoomParticipant {
  userId: string;
  username: string;
  role: 'host' | 'member';
  joinedAt: number;
}

export type ActivityBonusBreakdownItem =
  | { talentId: string; points: number }
  | { gearInstanceId: string; gearId: string; points: number };

export interface RoomActivity {
  id: string;
  userId: string;
  username: string;
  type: ActivityType;
  qty: number;
  basePoints: number;
  bonusPoints: number;
  bonusBreakdown: ActivityBonusBreakdownItem[];
  points: number;
  timestamp: number;
}

export interface Room {
  clubId?: string | null;
  clubDay?: string | null;
  id: string;
  workoutId: string;
  challengeType: ChallengeType;
  challengeSnapshot: RoomChallengeSnapshot;
  createdBy: string;
  date: string;
  goalPointsPerUser: number;
  participants: RoomParticipant[];
  participantPoints: Record<string, number>;
  activities: RoomActivity[];
  completed: boolean;
  completedAt: number | null;
  currentPoints: number;
  requiredPoints: number;
  createdAt: number;
  expiresAt: number;
  expired: boolean;
  archived: boolean;
  status: RoomStatus;
  revision: number;
  rewardClaimed: boolean;
}

export interface RoomInvite {
  id: string;
  roomId: string;
  status: RoomInviteStatus;
  createdAt: number;
  expiresAt: number;
  redeemedAt: number | null;
  redeemedByUserId: string | null;
  revokedAt: number | null;
}

export interface CreatedRoomInvite extends RoomInvite {
  token: string;
}
